import { loadOrderProductPrice } from 'modules/orders/hooks'
import { useOrderStore } from 'modules/orders/store'
import { useFormContext } from 'react-hook-form'
import { Promise as BBPromise } from 'bluebird'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import { useEffect, useRef } from 'react'
import { quantityToNumber } from 'utils'

type RecalculeProductsPriceInput = {
  products: any[]
  orderId: string
  customerId: string
  paymentTermId: string
  sellerId: string
  unityId: string
  orderTypeId: string
}

const PRICE_TOLERANCE = 0.001

const getTableBasePrice = (item: any, priceData: any) => {
  const defaultPrice = Number(priceData.defaultPrice)
  const defaultPrice4 = Number(
    priceData.defaultPrice4 ?? item.defaultPrice4 ?? 0
  )
  const saleType = item.saleType === '4' || item.saleType === 4 ? '4' : '12'
  const basePrice =
    saleType === '4' && defaultPrice4 > 0 ? defaultPrice4 : defaultPrice

  return { defaultPrice, defaultPrice4, saleType, basePrice }
}

/** Preço padrão do item na proposta (antes de negociação). */
const getOriginalItemPrice = (item: any) =>
  Number(item.originalItemPrice ?? item.currentPrice ?? item.defaultPrice ?? 0)

/** salePrice diferente do preço original = negociado. */
const isNegotiatedPrice = (item: any) => {
  const salePrice = Number(item.salePrice)
  if (salePrice <= 0) return false

  const originalPrice = getOriginalItemPrice(item)
  return Math.abs(salePrice - originalPrice) > PRICE_TOLERANCE
}

const calcDiscountFromPrices = (currentPrice: number, salePrice: number) => {
  const discount = Math.max(0, currentPrice - salePrice)
  const discountPercent =
    currentPrice > 0 ? Math.max(0, (1 - salePrice / currentPrice) * 100) : 0

  return { discount, discountPercent }
}

/**
 * Duplicação: mantém salePrice se negociado (salePrice ≠ preço original).
 * Na abertura ou ao trocar cliente, recalcula para a tabela só se não era negociado.
 */
const applyRecalculatedProductPriceForDuplicate = (
  item: any,
  priceData: any
) => {
  const { defaultPrice, defaultPrice4, saleType, basePrice } =
    getTableBasePrice(item, priceData)
  const originalItemPrice = item.originalItemPrice ?? getOriginalItemPrice(item)

  if (isNegotiatedPrice(item)) {
    const salePrice = Number(item.salePrice)
    const currentPrice = Number(item.currentPrice ?? item.defaultPrice ?? 0)
    const { discount, discountPercent } = calcDiscountFromPrices(
      currentPrice,
      salePrice
    )

    return {
      ...item,
      ...priceData,
      saleType,
      defaultPrice,
      defaultPrice4,
      currentPrice,
      salePrice,
      originalItemPrice,
      discount,
      discountPercent,
      origin: item.saleType === '4' ? priceData.origin ?? 0 : 0,
      icmsPercent: item.saleType === '4' ? 4 : priceData.icmsPercent
    }
  }

  return {
    ...item,
    ...priceData,
    saleType,
    defaultPrice,
    defaultPrice4,
    currentPrice: basePrice,
    salePrice: basePrice,
    originalItemPrice,
    discount: 0,
    discountPercent: 0,
    origin: item.saleType === '4' ? priceData.origin ?? 0 : 0,
    icmsPercent: item.saleType === '4' ? 4 : priceData.icmsPercent
  }
}

const recalculeProductsPrice = async (params: RecalculeProductsPriceInput) => {
  const {
    products,
    orderId,
    customerId,
    sellerId,
    unityId,
    paymentTermId,
    orderTypeId
  } = params

  const newProducts: any[] = []

  await BBPromise.map(
    products,
    async (item: any) => {
      const response = await loadOrderProductPrice({
        sequence: item.sequence,
        productId: item.product.id,
        configurationId: item?.configurationId || '',
        quantity: quantityToNumber(item.quantity),
        origin: Number(item.origin),
        orderId,
        customerId,
        paymentTermId,
        sellerId,
        unityId,
        saleTypeId: orderTypeId
      })

      const pricePayload = {
        ...response.data,
        origin: item.saleType === '4' ? response.data.origin ?? 0 : 0,
        icmsPercent: item.saleType === '4' ? 4 : response.data.icmsPercent
      }

      const product = applyRecalculatedProductPriceForDuplicate(
        item,
        pricePayload
      )

      newProducts.push(product)
    },
    { concurrency: 1 }
  )

  return newProducts
}

const buildProductsCacheKey = (products: any[]) =>
  JSON.stringify(
    products
      .map(
        (prod: any) =>
          `${prod.product?.id}-${prod.configurationId || ''}-${
            prod.sequence
          }-${quantityToNumber(prod.quantity)}`
      )
      .sort()
  )

export function useLoadNewOrderProductsPrice(
  query: RecalculeProductsPriceInput,
  options?: {
    enabled?: boolean
    recalcKey?: number
    keepPreviousData?: boolean
    onError?: () => void
  }
) {
  const { products, ...rest } = query
  const hookEnabled = options?.enabled ?? true

  const cache = {
    ...rest,
    recalcKey: options?.recalcKey,
    products: buildProductsCacheKey(products)
  }

  const hasValidProducts =
    !!query.products?.length &&
    query.products
      .map(
        (prod) =>
          !!prod.sequence &&
          !!prod.product?.id &&
          quantityToNumber(prod?.quantity) > 0
      )
      .every((ele) => ele)

  return useQuery(
    ['orderProductsPrice', cache],
    () => recalculeProductsPrice(query),
    {
      enabled:
        hookEnabled &&
        !!query.orderId &&
        hasValidProducts &&
        !!query.customerId &&
        !!query.paymentTermId &&
        !!query.sellerId &&
        !!query.unityId &&
        !!query.orderTypeId,
      keepPreviousData: options?.keepPreviousData ?? true,
      onError: () => {
        toast.error('Não foi possível recalcular os preços!', {
          toastId: 'loadOrderProductPrice'
        })
        options?.onError?.()
      }
    }
  )
}

type UseRecalculeItemsOptions = {
  enabled?: boolean
  productsOverride?: any[]
  recalcKey?: number
  onRecalcComplete?: (items: any[]) => void
  onRecalcError?: () => void
}

export const useRecalculeItems = (options?: UseRecalculeItemsOptions) => {
  const hookEnabled = options?.enabled ?? true
  const addProductsForm = useOrderStore.useAddProductsForm()
  const form = useOrderStore.useForm()
  const { watch } = useFormContext()
  const products = options?.productsOverride ?? form?.orderItems
  const lastAppliedRecalcKeyRef = useRef<number | undefined>()

  const [paymentTerm, customer, seller, unity, orderType] = watch([
    'paymentTerm',
    'customer',
    'seller',
    'unity',
    'orderType'
  ])

  const isDuplicateCustomerRecalc = !!options?.productsOverride?.length
  const recalcKey = options?.recalcKey
  const onRecalcComplete = options?.onRecalcComplete

  const { data, isFetching, isSuccess, dataUpdatedAt } =
    useLoadNewOrderProductsPrice(
      {
        products,
        orderId: form?.orderId || '',
        customerId: customer?.id,
        paymentTermId: paymentTerm?.id,
        sellerId: seller?.id,
        unityId: unity?.id,
        orderTypeId: orderType?.id
      },
      {
        enabled: hookEnabled,
        recalcKey: options?.recalcKey,
        keepPreviousData: !isDuplicateCustomerRecalc,
        onError: options?.onRecalcError
      }
    )

  const isRecalculatingDuplicateOrder =
    useOrderStore.useIsRecalculatingDuplicateOrder()

  useEffect(() => {
    if (!hookEnabled || isFetching || !data) return

    if (!isDuplicateCustomerRecalc && isRecalculatingDuplicateOrder) return

    const alreadyApplied =
      isDuplicateCustomerRecalc && lastAppliedRecalcKeyRef.current === recalcKey

    if (alreadyApplied) return

    if (isDuplicateCustomerRecalc) {
      lastAppliedRecalcKeyRef.current = recalcKey
    }

    addProductsForm(data)

    if (isDuplicateCustomerRecalc && isSuccess) {
      onRecalcComplete?.(data)
    }
  }, [
    addProductsForm,
    data,
    dataUpdatedAt,
    hookEnabled,
    isDuplicateCustomerRecalc,
    isFetching,
    isRecalculatingDuplicateOrder,
    isSuccess,
    onRecalcComplete,
    recalcKey
  ])

  return { isLoading: isFetching }
}
